import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon } from 'lucide-react';
import { getProductBySlug } from '../data/products';
import { ProductSpecs } from '../components/products/ProductSpecs';
import { Button } from '../components/ui/Button';
interface ProductDetailPageProps {
  onRequestQuote: (productId: string) => void;
}
export function ProductDetailPage({
  onRequestQuote
}: ProductDetailPageProps) {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const product = slug ? getProductBySlug(slug) : undefined;
  if (!product) {
    return <Navigate to="/products" replace data-id="element-743" />;
  }
  return <main className="min-h-screen bg-dark-bg" data-id="element-744">
      {/* Back Link */}
      <div className="bg-dark-surface border-b-2 border-dark-border" data-id="element-745">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-746">
          <Link to="/products" className="inline-flex items-center text-gray-400 hover:text-brand-yellow transition-colors focus:outline-none focus:ring-2 focus:ring-brand-yellow rounded-industrial" data-id="element-747">
            <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-748" />
            Nazad na proizvode
          </Link>
        </div>
      </div>

      {/* Product Hero */}
      <section className="py-12 bg-dark-surface border-b-2 border-dark-border" data-id="element-749">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-750">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-751">
            <div className="w-full bg-gradient-to-br from-gray-900 to-gray-800 rounded-industrial p-8 flex items-center justify-center min-h-[400px] lg:min-h-[500px]" data-id="element-752">
              <img src={product.image} alt={`${product.name} ${product.model}`} className="w-full h-auto object-contain max-h-[450px]" data-id="element-753" />
            </div>
            <div data-id="element-754">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-2" data-id="element-755">
                {product.name}
              </h1>
              <p className="text-2xl text-gray-400 mb-6" data-id="element-756">{product.model}</p>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed" data-id="element-757">
                {product.description}
              </p>
              <div className="mb-8" data-id="element-758">
                <div className="flex items-baseline gap-3" data-id="element-759">
                  <span className="text-5xl font-black text-brand-yellow" data-id="element-760">
                    €{product.price.toLocaleString()}
                  </span>
                  <span className="text-base text-gray-400" data-id="element-761">bez PDV</span>
                </div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-762">
                  Sa PDV: €{Math.round(product.price * 1.2).toLocaleString()} ·
                  Rok isporuke: 2 meseca od potvrde porudžbine.
                </p>
              </div>
              <Button variant="primary" size="lg" onClick={() => onRequestQuote(product.id)} data-id="element-763">
                Zatražite ponudu
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-12 bg-dark-bg" data-id="element-764">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-765">
          <h2 className="text-3xl font-black text-white mb-6" data-id="element-766">
            Ključne karakteristike
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-767">
            {product.highlights.map(highlight => <div key={highlight} className="flex items-center space-x-3 bg-dark-surface border-2 border-dark-border rounded-industrial p-4" data-id="element-768">
                <CheckIcon className="w-6 h-6 text-brand-yellow flex-shrink-0" data-id="element-769" />
                <span className="text-white font-semibold" data-id="element-770">{highlight}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-12 bg-dark-surface" data-id="element-771">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-772">
          <h2 className="text-3xl font-black text-white mb-6" data-id="element-773">
            Tehničke specifikacije
          </h2>
          <ProductSpecs specs={product.specs} data-id="element-774" />
        </div>
      </section>

      {/* Features */}
      <section className="py-12 bg-dark-bg" data-id="element-775">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-776">
          <h2 className="text-3xl font-black text-white mb-6" data-id="element-777">
            Funkcije i prednosti
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-778">
            {product.features.map(feature => <div key={feature} className="flex items-start space-x-3 bg-dark-surface border-2 border-dark-border rounded-industrial p-6" data-id="element-779">
                <CheckIcon className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" data-id="element-780" />
                <span className="text-gray-300" data-id="element-781">{feature}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-dark-surface border-t-2 border-dark-border" data-id="element-782">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-783">
          <h2 className="text-4xl font-black text-white mb-4" data-id="element-784">
            Spremni za poručivanje?
          </h2>
          <p className="text-xl text-gray-400 mb-8" data-id="element-785">
            Kontaktirajte nas za detaljnu ponudu i rok isporuke.
          </p>
          <Button variant="primary" size="lg" onClick={() => onRequestQuote(product.id)} data-id="element-786">
            Zatražite ponudu
          </Button>
        </div>
      </section>
    </main>;
}